import {
  authAtom,
  CloseIcon,
  DimmedBackground,
  FixedOuterWrapper,
  getPrimaryColor,
  Icon,
  PAGE_NAME,
  Styles,
  useRecoil,
} from '@orcar/common-d2c';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { app } from '@/constants/common';
import { slideIn } from '@/styles/animation';
import { GUIDE_ITEMS } from './CommonHeader';

type DrawerProps = {
  onClose: () => void;
  isDrawerOpen: boolean;
  handleLogin: () => void;
  handleLogout: () => void;
};

const Drawer = ({
  onClose,
  isDrawerOpen,
  handleLogin,
  handleLogout,
}: DrawerProps) => {
  const navigate = useNavigate();
  const { recoilValue: user } = useRecoil(authAtom);

  useEffect(() => {
    document.body.style.cssText = `
      position: fixed;
      top: -${window.scrollY}px;
      overflow-y: scroll;
      width: 100%;`;
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.cssText = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    };
  }, []);

  const go = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      <DimmedBackground onClick={onClose} topLevel />
      <FixedOuterWrapper>
        <Wrapper isDrawerOpen={isDrawerOpen}>
          <TopBar>
            <Icon
              src={CloseIcon}
              width={20}
              height={20}
              onClick={onClose}
              wrapperWidth='40px'
              wrapperHeight='40px'
            />
          </TopBar>

          <Content>
            {/* 예약 / 예약확인 빠른 진입 */}
            <QuickRow>
              <QuickBtn onClick={() => go(PAGE_NAME.search)}>예약</QuickBtn>
              <QuickBtn onClick={() => go(PAGE_NAME.find_reservation)}>
                예약확인
              </QuickBtn>
            </QuickRow>

            {/* 이용안내 (하위 6개) */}
            <Group>
              <GroupTitle>이용안내</GroupTitle>
              {GUIDE_ITEMS.map((item) => (
                <SubLink key={item.title} onClick={() => go(item.moveTo)}>
                  · {item.title}
                </SubLink>
              ))}
            </Group>

            <SectionLink onClick={() => go('/guide/insurance')}>
              보험안내
            </SectionLink>
            <SectionLink onClick={() => go(PAGE_NAME.cs_center)}>
              자주묻는질문
            </SectionLink>
            <SectionLink onClick={() => go(PAGE_NAME.event)}>
              이벤트
            </SectionLink>

            <Divider />

            {!!user ? (
              <>
                <SectionLink onClick={() => go(PAGE_NAME.my_page)}>
                  마이페이지
                </SectionLink>
                <LoginButton
                  onClick={() => {
                    handleLogout();
                    onClose();
                  }}
                >
                  로그아웃
                </LoginButton>
              </>
            ) : (
              <LoginButton
                onClick={() => {
                  onClose();
                  handleLogin();
                }}
              >
                로그인
              </LoginButton>
            )}
          </Content>
        </Wrapper>
      </FixedOuterWrapper>
    </>
  );
};

export default Drawer;

const Wrapper = styled.div<{ isDrawerOpen: boolean }>`
  position: fixed;
  display: flex;
  flex-direction: column;
  top: 0;
  left: 0;
  width: 300px;
  height: 100vh;
  padding: 16px 10px;
  background-color: ${Styles.COLOR.WHITE};
  z-index: 100;
  overflow-y: auto;
  animation: 0.3s ease-out ${slideIn};
`;

const TopBar = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 12px 30px;
  gap: 22px;
`;

const QuickRow = styled.div`
  display: flex;
  gap: 10px;
`;

const QuickBtn = styled.button`
  flex: 1;
  height: 56px;
  border: 1px solid ${Styles.COLOR.GREY_05};
  border-radius: 12px;
  background-color: ${Styles.COLOR.GREY_BG};
  ${Styles.FONT.H4};
  color: ${Styles.COLOR.GREY_01};
  cursor: pointer;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const GroupTitle = styled.p`
  ${Styles.FONT.H3};
  color: ${Styles.COLOR.GREY_01};
  margin: 0;
`;

const SubLink = styled.span`
  ${Styles.FONT.B1};
  color: ${Styles.COLOR.GREY_02};
  padding-left: 4px;
  cursor: pointer;
`;

const SectionLink = styled.span`
  ${Styles.FONT.H3};
  color: ${Styles.COLOR.GREY_01};
  cursor: pointer;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${Styles.COLOR.GREY_05};
  margin: 4px 0;
`;

const LoginButton = styled.button`
  height: 48px;
  ${({ theme }) => theme.FONT.H4};
  color: ${Styles.COLOR.WHITE};
  background-color: ${getPrimaryColor(app)};
  border: none;
  border-radius: 8px;
  cursor: pointer;
`;
